/**
 * Availability calendar + vacation mode.
 *
 * Two surfaces:
 *   - <AvailabilityCalendar />: editor for the freelancer's settings page
 *   - <AvailabilityBadge />:    read-only badge for any profile or modal
 *
 * Read/write goes through src/lib/availability.js, which persists to
 * localStorage and broadcasts an 'availability:changed' event so other
 * components (HireModal, ProposalModal) can react without prop-drilling.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Plane, Sun, Clock } from 'lucide-react';
import {
  loadAvailability,
  saveAvailability,
  resolveAvailability,
  availabilityLabel,
} from '../lib/availability';

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available now', icon: Sun },
  { value: 'limited', label: 'Limited (part-time)', icon: Clock },
  { value: 'vacation', label: 'On vacation', icon: Plane },
];

const useAvailability = () => {
  const [availability, setAvailability] = useState(() => loadAvailability());
  useEffect(() => {
    const onChange = (e) => setAvailability(e.detail);
    window.addEventListener('availability:changed', onChange);
    return () => window.removeEventListener('availability:changed', onChange);
  }, []);
  return availability;
};

export const AvailabilityCalendar = ({ onSaved }) => {
  const initial = useMemo(() => loadAvailability(), []);
  const [status, setStatus] = useState(initial.status);
  const [daysPerWeek, setDaysPerWeek] = useState(initial.daysPerWeek || 5);
  const [hoursPerWeek, setHoursPerWeek] = useState(initial.hoursPerWeek || 40);
  const [availableFrom, setAvailableFrom] = useState(initial.availableFrom?.slice(0, 10) || '');
  const [vacationUntil, setVacationUntil] = useState(initial.vacationUntil?.slice(0, 10) || '');
  const [savedAt, setSavedAt] = useState(initial.updatedAt);

  const handleSave = () => {
    const next = saveAvailability({
      status,
      daysPerWeek: Number(daysPerWeek) || 5,
      hoursPerWeek: Number(hoursPerWeek) || null,
      availableFrom: availableFrom ? new Date(availableFrom).toISOString() : null,
      vacationUntil: status === 'vacation' && vacationUntil
        ? new Date(vacationUntil).toISOString()
        : null,
    });
    setSavedAt(next.updatedAt);
    onSaved && onSaved(next);
  };

  const preview = resolveAvailability({
    status,
    daysPerWeek,
    hoursPerWeek,
    availableFrom: availableFrom || null,
    vacationUntil: vacationUntil || null,
  });

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Availability</h2>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Clients see this on your profile and can&apos;t invite you to new jobs while you&apos;re on vacation.
      </p>

      {/* Status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
        {STATUS_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = status === opt.value;
          return (
            <button
              type="button"
              key={opt.value}
              onClick={() => setStatus(opt.value)}
              className={`flex items-center gap-2 p-3 rounded-xl border-2 text-left transition ${
                active ? 'border-black bg-black text-white' : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {status === 'limited' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <label className="block text-sm">
            <span className="text-gray-700 font-medium">Days per week</span>
            <input
              type="number"
              min={1}
              max={7}
              value={daysPerWeek}
              onChange={(e) => setDaysPerWeek(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:ring-0"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700 font-medium">Hours per week</span>
            <input
              type="number"
              min={1}
              max={80}
              value={hoursPerWeek}
              onChange={(e) => setHoursPerWeek(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:ring-0"
            />
          </label>
        </div>
      )}

      {status === 'vacation' && (
        <label className="block text-sm mb-5">
          <span className="text-gray-700 font-medium">Back from vacation on</span>
          <input
            type="date"
            value={vacationUntil}
            onChange={(e) => setVacationUntil(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:ring-0"
          />
          <p className="text-xs text-gray-500 mt-1">
            Clients won&apos;t be able to invite you until this date.
          </p>
        </label>
      )}

      {status !== 'vacation' && (
        <label className="block text-sm mb-5">
          <span className="text-gray-700 font-medium">Available starting (optional)</span>
          <input
            type="date"
            value={availableFrom}
            onChange={(e) => setAvailableFrom(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-black focus:ring-0"
          />
        </label>
      )}

      <div className="flex items-center justify-between border-t border-gray-100 pt-4">
        <div className="text-sm text-gray-600">
          Preview:{' '}
          <span className="font-semibold text-gray-900">
            {availabilityLabel({
              status,
              daysPerWeek,
              hoursPerWeek,
              availableFrom: availableFrom || null,
              vacationUntil: vacationUntil || null,
            })}
          </span>
          {preview.state === 'vacation' && (
            <span className="ml-2 text-xs text-amber-700">Invites blocked</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          className="bg-black text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800"
        >
          Save
        </button>
      </div>

      {savedAt && (
        <p className="text-xs text-gray-400 mt-2">
          Last saved {new Date(savedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export const AvailabilityBadge = ({ availability: passed, className = '' }) => {
  const live = useAvailability();
  const availability = passed || live;
  const resolved = resolveAvailability(availability);
  const tone = {
    'available': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'available-soon': 'bg-sky-50 text-sky-700 border-sky-200',
    'limited': 'bg-amber-50 text-amber-700 border-amber-200',
    'vacation': 'bg-rose-50 text-rose-700 border-rose-200',
  }[resolved.state];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${tone} ${className}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {availabilityLabel(availability)}
    </span>
  );
};

export default AvailabilityCalendar;
